import {ChangeDetectionStrategy, Component, computed, ElementRef, Inject, Injectable, InjectionToken, input, signal, TemplateRef, viewChild} from "@angular/core";
import {ArkivnodeId, ArkivNodeModell, ArkivRessursClient, DokumentVersjonModell, DokumentVersjonOgSakerModell, TynnSakModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatMenu, MatMenuItem} from "@angular/material/menu";
import {MatIcon} from "@angular/material/icon";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {TranslateModule} from "@ngx-translate/core";
import {MatDivider} from "@angular/material/divider";
import {erIkkeTomListe, erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle} from "@angular/material/dialog";
import {FormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {ArkivFlyttNodeDialogKomponent} from "web-ng2/moduler/arkiv/modale/arkiv-flytt-node-dialog/arkiv-flytt-node-dialog";
import {ArkivmappeOpprettRedigerDialogKomponent} from "web-ng2/moduler/arkiv/modale/arkivmappe-opprett-rediger-dialog/arkivmappe-opprett-rediger-dialog";
import {firstValueFrom, Observable} from "rxjs";
import {ArkivImporterZipDialogKomponent} from "web-ng2/moduler/arkiv/modale/arkiv-importer-zip-dialog/arkiv-importer-zip-dialog";
import {ArkivDatatjeneste, ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {FilvelgerViaInputFelt} from "felles-ng2/tjenester/filopplasting/FilvelgerViaInputFelt";
import {SkjermingDialogKomponent} from "web-ng2/moduler/skjerming/modale/skjerming-dialog/skjerming-dialog";
import {SkjermetEntitetType} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {kanArkivnodeRedigeres, kanNyNodeLeggesTil} from "felles-ng2/modell/arkivModellHjelpere";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";

const debug = initDebugger('ArkivmappeMenyKomponent');

/**  InjectionToken for injisering av {@link DelegerteArkivmappeMenyOperasjoner} implementasjon. */
export const DelegerteArkivmappeMenyOperasjoner_DI_TOKEN = new InjectionToken<DelegerteArkivmappeMenyOperasjoner>('DelegerteArkivmappeMenyOperasjoner implementasjon');

/**
 * Arkivmappe meny
 */
@Component({
    selector: 'arkivmappe-meny',
    templateUrl: 'arkivmappe-meny.html',
    styleUrl: 'arkivmappe-meny.scss',
    imports: [
        MatMenu,
        MatIcon,
        MatMenuItem,
        BekreftKlikkDirektiv,
        TranslateModule,
        MatDivider,
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        MatDialogClose
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivmappeMenyKomponent {

    private mappeKanIkkeSlettesDialogTemplate = viewChild.required<TemplateRef<ElementRef>>('mappeKanIkkeSlettesDialogTemplate');
    private slettMappeMedBarnDialogTemplate = viewChild.required<TemplateRef<ElementRef>>('slettMappeMedBarnDialogTemplate');

    matMeny = viewChild.required(MatMenu);

    /** Mappen menyen gjelder for */
    mappe = input.required<ArkivMappenodeDLM>();

    redigeringMulig = computed(() => kanArkivnodeRedigeres(this.mappe(), this.kontekst.autentisertBruker()));
    leggTilNyNodeMulig = computed(() => kanNyNodeLeggesTil(this.mappe(), this.kontekst.autentisertBruker()));
    /** Liste med dokumenter som er referert i saker og som ligger i denne mappen eller en submappe,
     * settes av {@link #validerAtMappeKanSlettesRekursivt} */
    kanIkkeSlettesPgaBrukISaker = signal<DokumentVersjonOgSakerModell[]>([]);
    barnAvMappeSomSlettes = signal<ArkivNodeModell[]>([]);

    constructor(private avhengigheter: ArkivmappeMenyKomponentAvhengigheter,
                @Inject(DelegerteArkivmappeMenyOperasjoner_DI_TOKEN) private delegerteOperasjoner: DelegerteArkivmappeMenyOperasjoner,
                private dialog: MatDialog,
                private filvelger: FilvelgerViaInputFelt,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjon: Navigasjon) {
    }

    async slett() {
        let slettRekursivt = false;
        this.barnAvMappeSomSlettes.set(await this.barn());
        if (erIkkeTomListe(this.barnAvMappeSomSlettes())) {
            if (await this.validerAtMappeKanSlettesRekursivt())
                slettRekursivt = true;
            else return false; //kan ikke slette - dokumenter er knyttet til saker
        }

        await this.avhengigheter.slettRekursivt(this.mappe().id, slettRekursivt);
    }

    flytt() {
        ArkivFlyttNodeDialogKomponent.visFlyttMappe(this.dialog, this.mappe());
    }

    rediger() {
        ArkivmappeOpprettRedigerDialogKomponent.visRedigerMappe(this.dialog, this.mappe());
    }

    visSkjerming() {
        this.delegerteOperasjoner.visSkjerming(this.mappe());
    }

    redigerSkjerming() {
        SkjermingDialogKomponent.redigerForArkivmappe(this.dialog, this.mappe());
    }

    nyMappe() {
        ArkivmappeOpprettRedigerDialogKomponent.visOpprettMappe(this.dialog, this.mappe());
    }

    async nyttDokument() {
        this.delegerteOperasjoner.nyttDokument(this.mappe());
    }

    async importerArkivFraZip() {
        const valgteFiler = await this.filvelger.presenterFilvelger({tillateMimetyper: zipFiltype, tillatMultiple: false});
        if (erTomListe(valgteFiler)) return;
        ArkivImporterZipDialogKomponent.vis(this.dialog, this.mappe(), valgteFiler[0]).beforeClosed();
    }

    gaaTilDokument(dokumentVersjon: DokumentVersjonModell) {
        this.navigasjon.gåTilArkivdokumentKnyttetTilDokument(dokumentVersjon.dokumentId);
    }

    gaaTilSak(sak: TynnSakModell) {
        this.navigasjon.gåTilSak(sak.id);
    }

    // private hjelpemetoder

    private async barn(): Promise<ArkivNodeModell[]> {
        return await firstValueFrom(this.avhengigheter.barn(this.mappe().id));
    }

    private async validerAtMappeKanSlettesRekursivt(): Promise<boolean> {
        this.kanIkkeSlettesPgaBrukISaker.set(await this.avhengigheter.sakerKnyttetTilArkivdokumenter(this.mappe().id));
        if (erTomListe(this.kanIkkeSlettesPgaBrukISaker()))
            //be bruker bekrefte rekursiv sletting
            return  await firstValueFrom<boolean>(this.dialog.open(this.slettMappeMedBarnDialogTemplate(), {width: '500px'}).beforeClosed());

        //informer om at mappe ikke kan slettes
        this.dialog.open(this.mappeKanIkkeSlettesDialogTemplate(), {width: '500px'})
        return false;
    };

}

const zipFiltype = 'application/zip, application/x-zip-compressed, multipart/x-zip, .zip';


/** Operasjoner som arkivmappe-meny-komponenten ikke selv kan realisere, men som noen høyere opp i komponent-treet må realisere */
export interface DelegerteArkivmappeMenyOperasjoner {

    /** Viser skjermingsdetaljer for angitt mappe. */
    visSkjerming(arkivmappe: ArkivMappenodeDLM): void;

    /** Presenterer bruker med en fil-velger-dialog og registrerer valgte filer i angitt mappe. */
    nyttDokument(arkivmappe: ArkivMappenodeDLM): Promise<void>;
}

@Injectable()
export class ArkivmappeMenyKomponentAvhengigheter {

    constructor(private arkivDatatjeneste: ArkivDatatjeneste, private arkivRessurs: ArkivRessursClient, private dialog: MatDialog) {
    }

    barn(forelderId: ArkivnodeId): Observable<ArkivNodeModell[]> {
        return this.arkivDatatjeneste.hentBarnFraLokallager(forelderId);
    }

    async sakerKnyttetTilArkivdokumenter(mappeId: ArkivnodeId): Promise<DokumentVersjonOgSakerModell[]> {
        return this.arkivRessurs.sakerKnyttetTilArkivdokumenterIMappe(mappeId).somPromise();
    }

    async slettRekursivt(mappeId: ArkivnodeId, rekursivt: boolean): Promise<void> {
        await this.arkivDatatjeneste.slettMappe(mappeId, {rekursivt})
    }
}
